import { Dropdown, Field as DropField, Item, Menu, Select } from "@zendeskgarden/react-dropdowns";
import { Field, Input, Label, Message, Toggle } from "@zendeskgarden/react-forms";
import { Col, Row } from "@zendeskgarden/react-grid";
import React from "react";
import { Button } from "react-bootstrap";
import { withRouter } from "react-router";
import Loading from "../../../components/loading";
import { OPPORTUNITY_APPLICATION_FORM_ANSWERS_ROUTE } from "../../../providers/routes";
import { AlertService } from "../../../services/alert.service";
import { AuthenticationService } from "../../../services/auth.service";
import OpportunityService from "../../../services/opportunity.service";
import { t } from "../../../translations/translation.service";
import style from "./index.module.scss";

const MAX_TEXT_LENGTH = 150;
const SELECTION_TYPES = new Set(["combo", "multi_select", "single_select"]);

export interface ApplicationFormQuestion {
  questionId?: string;
  text: string;
  type: string;
  required: boolean;
  options: string[];
  parameter: { label: string; value: string };
  suggestedSkill: string;
}

const emptyQuestion = (): ApplicationFormQuestion => ({
  text: "",
  type: "text",
  required: false,
  options: [],
  parameter: { label: "", value: "" },
  suggestedSkill: "",
});

class OpportunityApplicationForm extends React.Component<any> {
  state = {
    loading: true,
    saving: false,
    message: "",
    error: "",
    opportunityTitle: "",
    questions: [] as ApplicationFormQuestion[],
    suggestions: [] as { name: string }[],
  };

  questionTypes = [
    { value: "combo", label: t("TipoRespostaComboBox") },
    { value: "multi_select", label: t("TipoRespostaMultiplaSelecao") },
    { value: "single_select", label: t("TipoRespostaSelecaoUnica") },
    { value: "text", label: t("TipoRespostaTexto") },
  ];

  constructor(props: any) {
    super(props);
    this.addQuestion = this.addQuestion.bind(this);
    this.save = this.save.bind(this);
    this.openAnswers = this.openAnswers.bind(this);
  }

  componentDidMount() {
    const id = this.props.match.params.id;
    if (!id) {
      this.setState({ loading: false, message: t("Vaga não encontrada") });
      return;
    }

    Promise.all([
      OpportunityService.getApplicationForm(id),
      OpportunityService.getApplicationFormSkillSuggestions(id),
    ])
      .then(([formResp, suggestionsResp]) => {
        this.setState({
          loading: false,
          opportunityTitle: formResp.data.opportunityTitle || "",
          questions: (formResp.data.questions || []).map((question: any) => ({
            ...emptyQuestion(),
            ...question,
            parameter: { ...emptyQuestion().parameter, ...question.parameter },
            options: question.options || [],
          })),
          suggestions: suggestionsResp.data || [],
        });
      })
      .catch((error) => {
        console.error(error);
        this.setState({ loading: false, message: t("Vaga não encontrada") });
      });
  }

  updateQuestion(index: number, changes: Partial<ApplicationFormQuestion>) {
    this.setState((prevState: any) => ({
      questions: prevState.questions.map((question: ApplicationFormQuestion, i: number) =>
        i === index ? { ...question, ...changes } : question
      ),
    }));
  }

  addQuestion(question?: ApplicationFormQuestion) {
    this.setState((prevState: any) => ({
      questions: [...prevState.questions, question || emptyQuestion()],
    }));
  }

  toggleRequired(index: number) {
    this.setState((prevState: any) => ({
      questions: prevState.questions.map((question: ApplicationFormQuestion, i: number) =>
        i === index ? { ...question, required: !question.required } : question
      ),
    }));
  }

  removeQuestion(index: number) {
    this.setState((prevState: any) => ({
      questions: prevState.questions.filter((_: ApplicationFormQuestion, i: number) => i !== index),
    }));
  }

  addSuggestion(skill: string) {
    this.addQuestion({
      ...emptyQuestion(),
      text: `${t("SugestaoPerguntaSkill")} ${skill}?`.substring(0, MAX_TEXT_LENGTH),
      suggestedSkill: skill,
      parameter: { label: t("ParametroTempoExperiencia"), value: "" },
    });
  }

  addOption(index: number) {
    this.setState((prevState: any) => ({
      questions: prevState.questions.map((question: ApplicationFormQuestion, i: number) =>
        i === index ? { ...question, options: [...question.options, ""] } : question
      ),
    }));
  }

  updateOption(index: number, optionIndex: number, value: string) {
    this.setState((prevState: any) => ({
      questions: prevState.questions.map((question: ApplicationFormQuestion, i: number) =>
        i === index
          ? {
              ...question,
              options: question.options.map((option, j) => (j === optionIndex ? value : option)),
            }
          : question
      ),
    }));
  }

  removeOption(index: number, optionIndex: number) {
    this.setState((prevState: any) => ({
      questions: prevState.questions.map((question: ApplicationFormQuestion, i: number) =>
        i === index
          ? { ...question, options: question.options.filter((_, j) => j !== optionIndex) }
          : question
      ),
    }));
  }

  validate(): string {
    const { questions } = this.state;

    for (const question of questions) {
      if (!question.text.trim()) {
        return t("ErroPerguntaSemEnunciado");
      }
      if (question.text.length > MAX_TEXT_LENGTH) {
        return t("ErroPerguntaTamanhoMaximo");
      }
      if (
        SELECTION_TYPES.has(question.type) &&
        question.options.filter((option) => option.trim()).length === 0
      ) {
        return t("ErroPerguntaSemOpcoes");
      }
    }

    return "";
  }

  save() {
    const error = this.validate();
    if (error) {
      this.setState({ error });
      return;
    }

    this.setState({ error: "", saving: true });
    OpportunityService.updateApplicationForm(this.props.match.params.id, this.state.questions)
      .then(() => {
        this.setState({ saving: false });
        AlertService.success(t("FormularioCandidaturaSalvo"));
      })
      .catch((err) => {
        console.error(err);
        this.setState({
          saving: false,
          error: err?.response?.data?.message || t("ErroSalvarFormularioCandidatura"),
        });
      });
  }

  openAnswers() {
    this.props.history.push(
      OPPORTUNITY_APPLICATION_FORM_ANSWERS_ROUTE.replace(":id", this.props.match.params.id)
    );
  }

  renderQuestion(question: ApplicationFormQuestion, index: number) {
    const selectedType = this.questionTypes.find((type) => type.value === question.type);

    return (
      <div className={style["question-card"]} key={`question-${index}`}>
        <Row>
          <Col sm={12}>
            <Field>
              <Label>{`${t("Pergunta")} ${index + 1}`}</Label>
              <Input
                value={question.text}
                maxLength={MAX_TEXT_LENGTH}
                onChange={(event) => this.updateQuestion(index, { text: event.target.value })}
              />
              <span className={style["counter"]}>{`${question.text.length}/${MAX_TEXT_LENGTH}`}</span>
            </Field>
          </Col>
          <Col sm={6}>
            <Dropdown
              selectedItem={selectedType}
              onSelect={(type: any) =>
                this.updateQuestion(index, {
                  type: type.value,
                  options: SELECTION_TYPES.has(type.value) ? question.options : [],
                })
              }
              downshiftProps={{ itemToString: (item: any) => item?.label }}
            >
              <DropField>
                <Label>{t("TipoDeResposta")}</Label>
                <Select>{selectedType?.label}</Select>
              </DropField>
              <Menu>
                {this.questionTypes.map((type) => (
                  <Item key={type.value} value={type}>
                    {type.label}
                  </Item>
                ))}
              </Menu>
            </Dropdown>
          </Col>
          <Col sm={6}>
            <Field className="mt-4">
              <Toggle
                checked={question.required}
                onChange={() => this.toggleRequired(index)}
              >
                <Label>{t("RespostaObrigatoria")}</Label>
              </Toggle>
            </Field>
          </Col>
          {SELECTION_TYPES.has(question.type) && (
            <Col sm={12}>
              <Label className={`mt-3 ${style["block-label"]}`}>
                {t("OpcoesDeResposta")}
              </Label>
              {question.options.map((option, optionIndex) => (
                <Field className="mt-1" key={`option-${index}-${optionIndex}`}>
                  <Row>
                    <Col sm={9}>
                      <Input
                        value={option}
                        maxLength={MAX_TEXT_LENGTH}
                        onChange={(event) =>
                          this.updateOption(index, optionIndex, event.target.value)
                        }
                      />
                    </Col>
                    <Col sm={3}>
                      <Button
                        className={style["link-button"]}
                        onClick={() => this.removeOption(index, optionIndex)}
                      >
                        {t("Remover")}
                      </Button>
                    </Col>
                  </Row>
                </Field>
              ))}
              <Button className={style["link-button"]} onClick={() => this.addOption(index)}>
                {t("AdicionarOpcao")}
              </Button>
            </Col>
          )}
          <Col sm={6}>
            <Field className="mt-2">
              <Label>{t("ParametroDaResposta")}</Label>
              <Input
                value={question.parameter.label}
                maxLength={MAX_TEXT_LENGTH}
                placeholder={t("ParametroTempoExperiencia")}
                onChange={(event) =>
                  this.updateQuestion(index, {
                    parameter: { ...question.parameter, label: event.target.value },
                  })
                }
              />
            </Field>
          </Col>
          <Col sm={6}>
            <Field className="mt-2">
              <Label>{t("ValorEsperadoDoParametro")}</Label>
              <Input
                value={question.parameter.value}
                maxLength={MAX_TEXT_LENGTH}
                onChange={(event) =>
                  this.updateQuestion(index, {
                    parameter: { ...question.parameter, value: event.target.value },
                  })
                }
              />
            </Field>
          </Col>
          <Col sm={12} className="text-right">
            <Button className={style["link-button"]} onClick={() => this.removeQuestion(index)}>
              {t("RemoverPergunta")}
            </Button>
          </Col>
        </Row>
      </div>
    );
  }

  render() {
    if (this.state.loading) {
      return <Loading show={true} />;
    }

    if (this.state.message) {
      return <div className={style["container"]}>{this.state.message}</div>;
    }

    return (
      <div className={style["container"]}>
        <div className={style["header"]}>
          <h1 className={style["title"]}>{t("FormularioCandidatura")}</h1>
          {AuthenticationService.canViewApplicationFormAnswers() && (
            <Button className={style["action-button"]} onClick={this.openAnswers}>
              {t("VisualizarRespostas")}
            </Button>
          )}
        </div>
        <h2 className={style["subtitle"]}>{this.state.opportunityTitle}</h2>

        {this.state.suggestions.length > 0 && (
          <div className={style["suggestions"]}>
            <Label>{t("SkillsSugeridas")}</Label>
            <div>
              {this.state.suggestions.map((suggestion) => (
                <Button
                  key={suggestion.name}
                  className={style["suggestion-button"]}
                  onClick={() => this.addSuggestion(suggestion.name)}
                >
                  {suggestion.name}
                </Button>
              ))}
            </div>
          </div>
        )}

        {this.state.questions.map((question, index) => this.renderQuestion(question, index))}

        {this.state.error && <Message validation="error">{this.state.error}</Message>}

        <div className={style["actions"]}>
          <Button className={style["action-button"]} onClick={() => this.addQuestion()}>
            {t("AdicionarPergunta")}
          </Button>
          <Button
            className={style["action-button"]}
            disabled={this.state.saving}
            onClick={this.save}
          >
            {t("Salvar")}
          </Button>
        </div>
      </div>
    );
  }
}

export default withRouter(OpportunityApplicationForm);
